import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { HeartPulse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PortalError,
  requestOtp,
  resolveClinic,
  safePortalPath,
  selectProfile,
  setPortalSession,
  verifyOtp,
  verifyPin,
  type ClinicInfo,
  type ProfileOption,
  type VerifyOtpResult,
} from "./portalApi";

/**
 * PUBLIC patient-portal login. One page, three steps:
 *   (a) clinic  — "Mã phòng khám" (skipped when the URL carries :slug)
 *   (b) phone   — "Gửi mã OTP"  (request-otp; shows devCode hint in dev). The
 *                 answer is the same whether or not the number has a record
 *                 (#19145) — so this page never says "chưa có hồ sơ"; it says
 *                 "nếu đã có hồ sơ, mã sẽ được gửi" and points to the clinic.
 *   (c) code    — "Đăng nhập"   (verify-otp; stores portal session -> /portal/me)
 *
 * Mounted OUTSIDE RequireAuth (sibling of /login, /view-order) and uses the
 * bare portalApi (never the staff apiClient), with its own localStorage keys.
 */

type Step = "clinic" | "phone" | "code" | "profile" | "pin";

const GENDER: Record<string, string> = { M: "Nam", F: "Nữ", OTHER: "Khác" };

export function PortalLoginPage() {
  const navigate = useNavigate();
  const params = useParams();
  const slug = params.slug ?? "";
  // Trang trong cổng cần quay lại sau khi đăng nhập (vd. /portal/slip?u=… từ mã QR).
  const [search] = useSearchParams();
  const next = safePortalPath(search.get("next"));

  const [clinic, setClinic] = useState<ClinicInfo | null>(null);
  const [slugInput, setSlugInput] = useState(slug);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  // #19148 — after the OTP, a patient with a PIN gets a token that can only
  // call verify-pin. Held in memory for this step, never stored.
  const [pending, setPending] = useState<VerifyOtpResult | null>(null);
  const [pin, setPin] = useState("");
  // #19147 — several records on one phone: pick one after the OTP.
  const [choice, setChoice] = useState<{
    token: string;
    profiles: ProfileOption[];
  } | null>(null);

  const [step, setStep] = useState<Step>(slug ? "phone" : "clinic");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  const errMsg = (err: unknown, fallback: string) =>
    err instanceof PortalError ? err.message : fallback;

  const doResolveClinic = useCallback(async (code: string) => {
    const c = await resolveClinic(code.trim());
    setClinic(c);
    setStep("phone");
    setError(null);
  }, []);

  // With a :slug in the URL, auto-resolve the clinic on mount.
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    setResolving(true);
    doResolveClinic(slug)
      .catch((err) => {
        if (!alive) return;
        setError(errMsg(err, "Không tìm thấy phòng khám."));
        setStep("clinic");
      })
      .finally(() => {
        if (alive) setResolving(false);
      });
    return () => {
      alive = false;
    };
  }, [slug, doResolveClinic]);

  async function onSubmitClinic(e: React.FormEvent) {
    e.preventDefault();
    if (!slugInput.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await doResolveClinic(slugInput);
    } catch (err) {
      setError(errMsg(err, "Không tìm thấy phòng khám với mã này."));
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitPhone(e: React.FormEvent) {
    e.preventDefault();
    if (!clinic || !phone.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await requestOtp(clinic.tenantId, phone.trim());
      setDevCode(res.devCode ?? null);
      setStep("code");
    } catch (err) {
      setError(errMsg(err, "Chưa gửi được yêu cầu. Vui lòng thử lại."));
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitCode(e: React.FormEvent) {
    e.preventDefault();
    if (!clinic || code.trim().length < 4) return;
    setBusy(true);
    setError(null);
    try {
      const res = await verifyOtp(clinic.tenantId, phone.trim(), code.trim());
      if (res.selectionToken && res.profiles?.length) {
        setChoice({ token: res.selectionToken, profiles: res.profiles });
        setStep("profile");
        return;
      }
      afterSession(res);
    } catch (err) {
      setError(errMsg(err, "Mã OTP không đúng hoặc đã hết hạn."));
    } finally {
      setBusy(false);
    }
  }

  function afterSession(res: VerifyOtpResult) {
    if (res.pinRequired) {
      setPending(res);
      setStep("pin");
      return;
    }
    openSession(res, res.patientName);
  }

  async function onPickProfile(patientId: string) {
    if (!choice) return;
    setBusy(true);
    setError(null);
    try {
      afterSession(await selectProfile(choice.token, patientId));
    } catch (err) {
      // The token is single-use: whatever went wrong, start the login again.
      setChoice(null);
      setStep("phone");
      setCode("");
      setError(
        errMsg(err, "Phiên chọn hồ sơ đã hết hạn. Vui lòng đăng nhập lại."),
      );
    } finally {
      setBusy(false);
    }
  }

  function openSession(res: VerifyOtpResult, patientName: string) {
    if (!clinic) return;
    setPortalSession({
      accessToken: res.accessToken,
      tenantId: clinic.tenantId,
      patientName,
      clinicName: clinic.clinicName,
      expiresAt: res.expiresAt ?? null,
    });
    navigate(next ?? "/portal/me", { replace: true });
  }

  async function onSubmitPin(e: React.FormEvent) {
    e.preventDefault();
    if (!pending || pin.trim().length < 4) return;
    setBusy(true);
    setError(null);
    try {
      const r = await verifyPin(pending.accessToken, pin.trim());
      if (r.ok && r.session) {
        openSession(r.session, pending.patientName);
        return;
      }
      setPin("");
      if (r.lockedUntil) {
        setError(
          `Bạn đã nhập sai mã PIN quá nhiều lần. Vui lòng thử lại sau ${new Date(
            r.lockedUntil,
          ).toLocaleTimeString("vi-VN", {
            hour: "2-digit",
            minute: "2-digit",
          })}.`,
        );
      } else {
        setError(`Mã PIN không đúng. Bạn còn ${r.remainingAttempts} lần thử.`);
      }
    } catch (err) {
      setError(errMsg(err, "Chưa kiểm tra được mã PIN. Vui lòng thử lại."));
    } finally {
      setBusy(false);
    }
  }

  function resetToClinic() {
    setClinic(null);
    setStep("clinic");
    setPhone("");
    setCode("");
    setDevCode(null);
    setPending(null);
    setPin("");
    setChoice(null);
    setError(null);
  }

  function backToPhone() {
    setStep("phone");
    setCode("");
    setDevCode(null);
    setPending(null);
    setPin("");
    setChoice(null);
    setError(null);
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 via-background to-emerald-50 px-4 pb-12 pt-6 font-sans text-foreground">
      <div className="flex justify-center">
        <Card className="mt-[6vh] w-full max-w-md rounded-2xl shadow-lg">
          <CardContent className="p-6">
            <div className="mb-5 text-center">
              <div
                className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground"
                aria-hidden
              >
                <HeartPulse className="h-7 w-7" />
              </div>
              <h1 className="text-xl font-semibold tracking-tight">
                Cổng tra cứu bệnh nhân
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Đăng nhập bằng số điện thoại để xem hồ sơ của bạn
              </p>
            </div>

            {clinic && (
              <div className="mb-4 text-center">
                <div className="text-lg font-bold text-primary">
                  {clinic.clinicName}
                </div>
                <button
                  type="button"
                  className="mt-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                  onClick={resetToClinic}
                >
                  Đổi phòng khám
                </button>
              </div>
            )}

            {resolving && (
              <p className="text-sm text-muted-foreground">
                Đang tải thông tin phòng khám…
              </p>
            )}

            {/* Step (a) — clinic code */}
            {step === "clinic" && !resolving && (
              <form onSubmit={onSubmitClinic} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pp-slug">Mã phòng khám</Label>
                  <Input
                    id="pp-slug"
                    autoFocus
                    placeholder="Ví dụ: phongkham-abc"
                    value={slugInput}
                    onChange={(e) => setSlugInput(e.target.value)}
                  />
                </div>
                <Button
                  className="w-full"
                  type="submit"
                  disabled={busy || !slugInput.trim()}
                >
                  {busy ? "Đang kiểm tra…" : "Tiếp tục"}
                </Button>
              </form>
            )}

            {/* Step (b) — phone */}
            {step === "phone" && clinic && (
              <form onSubmit={onSubmitPhone} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pp-phone">Số điện thoại</Label>
                  <Input
                    id="pp-phone"
                    type="tel"
                    inputMode="tel"
                    autoFocus
                    placeholder="0xxxxxxxxx"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  <p className="text-sm text-muted-foreground">
                    Nhập số điện thoại bạn đã đăng ký tại phòng khám.
                  </p>
                </div>
                <Button
                  className="w-full"
                  type="submit"
                  disabled={busy || !phone.trim()}
                >
                  {busy ? "Đang gửi…" : "Gửi mã OTP"}
                </Button>
              </form>
            )}

            {/* Step (c) — OTP code */}
            {step === "code" && clinic && (
              <form onSubmit={onSubmitCode} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pp-code">Nhập mã OTP</Label>
                  <p className="text-sm text-muted-foreground">
                    Nếu số {phone} đã có hồ sơ tại phòng khám, mã OTP sẽ được
                    gửi tới số này. Không nhận được mã sau 2 phút, vui lòng liên
                    hệ phòng khám.
                  </p>
                  <Input
                    id="pp-code"
                    className="h-12 text-center text-2xl font-bold tracking-[0.5em]"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    autoFocus
                    placeholder="••••••"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  />
                  {devCode && (
                    <div className="mt-2 rounded-md border border-dashed border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                      Mã OTP (dev): <b className="tracking-widest">{devCode}</b>
                    </div>
                  )}
                </div>
                <Button
                  className="w-full"
                  type="submit"
                  disabled={busy || code.trim().length < 4}
                >
                  {busy ? "Đang đăng nhập…" : "Đăng nhập"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={backToPhone}
                >
                  ← Đổi số điện thoại
                </Button>
              </form>
            )}

            {/* Step (c2) — one phone, several records (#19147) */}
            {step === "profile" && clinic && choice && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <div className="text-sm font-medium">Chọn hồ sơ cần xem</div>
                  <p className="text-sm text-muted-foreground">
                    Số điện thoại này đang gắn với nhiều hồ sơ tại phòng khám.
                  </p>
                </div>
                {choice.profiles.map((p) => (
                  <Button
                    key={p.patientId}
                    type="button"
                    variant="outline"
                    className="h-auto w-full justify-between py-3"
                    disabled={busy}
                    onClick={() => void onPickProfile(p.patientId)}
                  >
                    <span className="font-semibold">{p.maskedName}</span>
                    <span className="text-sm text-muted-foreground">
                      {[
                        p.birthYear ? `Sinh năm ${p.birthYear}` : null,
                        p.gender ? (GENDER[p.gender] ?? p.gender) : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </Button>
                ))}
              </div>
            )}

            {/* Step (d) — PIN (#19148): the patient set one in the Mini App */}
            {step === "pin" && clinic && (
              <form onSubmit={onSubmitPin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pp-pin">Nhập mã PIN</Label>
                  <p className="text-sm text-muted-foreground">
                    Bạn đã đặt mã PIN để bảo vệ kết quả khám. Nhập mã PIN đã đặt
                    trong Zalo Mini App của phòng khám.
                  </p>
                  <Input
                    id="pp-pin"
                    className="h-12 text-center text-2xl font-bold tracking-[0.5em]"
                    type="password"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={6}
                    autoFocus
                    placeholder="••••"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
                <Button
                  className="w-full"
                  type="submit"
                  disabled={busy || pin.trim().length < 4}
                >
                  {busy ? "Đang kiểm tra…" : "Xác nhận"}
                </Button>
              </form>
            )}

            {error && (
              <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
                {error}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
